import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { object, number, string, date, InferType } from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@heroui/react";
import { PlantingPlan, SimulatedCropPlan } from "@/types/cropPlan";
import {
    PlantingOperation,
    DEFAULT_SEEDING_RATE,
    DEFAULT_PLANTING_DEPTH,
} from "@/types/operations";
import { CropConfiguration } from "@/types/cropConfiguration";
import {
    useMemo,
    useState,
    useCallback,
    SetStateAction,
    Dispatch,
} from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faCheckCircle,
    faArrowRightToBracket,
    faTrash,
    faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { parseDate } from "@internationalized/date";
import TextInput from "@/components/inputs/TextInput";
import SelectInput from "@/components/inputs/SelectInput";
import DateInput from "@/components/inputs/DateInput";
import NumberInput from "@/components/inputs/NumberInput";
import AutoCompleteInput from "@/components/inputs/AutoCompleteInput";
import { UseQueryResult } from "@tanstack/react-query";
import ExpandingTable from "@/components/table/ExpandingTable";
import {
    ColumnDef,
    OnChangeFn,
    Row,
    RowSelectionState,
    Updater,
} from "@tanstack/react-table";
import { plantingPlanColumns } from "./plannerColumns";
import useConfirm from "@/lib/confirm/useConfirm";

const DEFAULT_CROP_CONFIGURATION_ID = 1;
const SENTINEL_ORGANIZATION_ID = 1;

const plantingPlanSchema = object({
    id: number().nullable(),
    date: date().required(),
    crop_configuration: number().required(),
    crm: number().required(),
    hybrid: string().nullable(),
    seeding_rate: number().required(),
    seeding_depth: number().nullable(),
    name: string().required(),
    organization: number().required(),
});

type PlantingPlanFormValues = InferType<typeof plantingPlanSchema>;

export function PlantingPlanForm({
    organizations,
    activePlantingOperationId,
    setSelectedPlantingPlanId,
    plantingPlansRequest,
    cropsQuery,
    selectedPlantingPlanId,
    primaryCropPlan,
}: {
    organizations: { id: number; description: string }[];
    activePlantingOperationId?: number;
    setSelectedPlantingPlanId: Dispatch<SetStateAction<number | null>>;
    plantingPlansRequest: UseQueryResult<PlantingPlan[]>;
    cropsQuery: UseQueryResult<CropConfiguration[]>;
    selectedPlantingPlanId: number | null;
    primaryCropPlan?: SimulatedCropPlan;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();

    const { user, userIsSentinel, isGrower } = useAuth();

    const userOrganizationId: number | undefined = useMemo(() => {
        if (typeof user?.organization === "object") {
            return user.organization.id;
        }
        return typeof user?.organization === "string"
            ? parseInt(user.organization)
            : user?.organization;
    }, [user]);

    const defaultPlantingDate = useMemo(() => {
        if (!cropsQuery.data) {
            return undefined;
        }
        const defaultCrop = cropsQuery.data.find(
            (crop) => crop.id === DEFAULT_CROP_CONFIGURATION_ID,
        );
        if (defaultCrop?.default_planting_date) {
            return dayjs(defaultCrop.default_planting_date).toDate();
        }
        return undefined;
    }, [cropsQuery.data]);

    const cropOptions = useMemo(() => {
        if (!cropsQuery.data) {
            return [];
        }
        return cropsQuery.data
            .sort((a, b) => a.crop_name.localeCompare(b.crop_name))
            .map((crop) => ({
                id: crop.id,
                description: crop.crop_name,
            }));
    }, [cropsQuery.data]);

    const plantingPlanForm = useForm<PlantingPlanFormValues>({
        resolver: yupResolver(plantingPlanSchema),
        defaultValues: {
            id: null,
            organization: userOrganizationId,
            date: defaultPlantingDate,
            crop_configuration: DEFAULT_CROP_CONFIGURATION_ID,
            crm: 100,
            hybrid: null,
            seeding_rate: DEFAULT_SEEDING_RATE,
            seeding_depth: DEFAULT_PLANTING_DEPTH,
        },
    });

    const twoMonthsAgo = useMemo(() => {
        return parseDate(dayjs().subtract(2, "month").format("YYYY-MM-DD"));
    }, []);

    const [plantingPlanId, plantingPlanOrganization] = plantingPlanForm.watch([
        "id",
        "organization",
    ]);

    const plantingOperationRequest = useGetRequest<PlantingOperation>({
        url: `/planting_operations/${activePlantingOperationId}/`,
        enabled: !!activePlantingOperationId,
    });

    const userCanEditPlantingPlan = useMemo(() => {
        return (
            userIsSentinel ||
            (!isGrower &&
                plantingPlanOrganization !== SENTINEL_ORGANIZATION_ID) ||
            plantingPlanOrganization === userOrganizationId
        );
    }, [
        plantingPlanOrganization,
        userOrganizationId,
        userIsSentinel,
        isGrower,
    ]);

    const [editPlantingPlanId, setEditPlantingPlanId] = useState<number | null>(
        null,
    );

    const savePlantingPlanMutation = useMutateRequest<PlantingPlan>({
        successMessage: "Planting Plan Saved",
        onSuccess: () => {
            setEditPlantingPlanId(null);
            plantingPlansRequest.refetch();
        },
    });

    const [addNewPlan, setAddNewPlan] = useState<boolean>(false);

    const handleSavePlantingPlan = useCallback(() => {
        const plantingData = {
            id: plantingPlanId,
            organization: plantingPlanForm.getValues("organization"),
            name: plantingPlanForm.getValues("name"),
            date: dayjs(plantingPlanForm.getValues("date")).format(
                "YYYY-MM-DD",
            ),
            crop_configuration:
                plantingPlanForm.getValues("crop_configuration"),
            seeding_rate: plantingPlanForm.getValues("seeding_rate"),
            seeding_depth: plantingPlanForm.getValues("seeding_depth"),
            varieties: [
                {
                    id: null,
                    name: plantingPlanForm.getValues("hybrid"),
                    crm: plantingPlanForm.getValues("crm"),
                },
            ],
        };
        if (plantingPlanId) {
            savePlantingPlanMutation.mutate(
                {
                    url: `/planting_plans/${plantingPlanId}/`,
                    method: "PUT",
                    data: plantingData,
                },
                {
                    onSuccess: () => {
                        setEditPlantingPlanId(null);
                        setAddNewPlan(false);
                        plantingPlansRequest.refetch();
                    },
                },
            );
        } else {
            savePlantingPlanMutation.mutate(
                {
                    url: `/planting_plans/`,
                    method: "POST",
                    data: plantingData,
                },
                {
                    onSuccess: () => {
                        setAddNewPlan(false);
                        setEditPlantingPlanId(null);
                        plantingPlansRequest.refetch();
                    },
                },
            );
        }
    }, [
        plantingPlanId,
        plantingPlanForm,
        savePlantingPlanMutation,
        plantingPlansRequest,
        setAddNewPlan,
        setEditPlantingPlanId,
    ]);

    const handleEditPlantingPlan = useCallback(
        (plan: PlantingPlan) => {
            setEditPlantingPlanId(plan.id);
            plantingPlanForm.reset({
                id: plan.id,
                name: plan.name,
                date: dayjs(plan.date).toDate(),
                organization: plan.organization,
                crop_configuration: plan.crop_configuration,
                crm: plan.varieties?.[0]?.crm || undefined,
                hybrid: plan.varieties?.[0]?.name || undefined,
                seeding_rate: plan.seeding_rate || undefined,
                seeding_depth: plan.seeding_depth || undefined,
            });
        },
        [plantingPlanForm, setEditPlantingPlanId],
    );

    const data = useMemo(() => {
        return (plantingPlansRequest?.data ?? []).map((plan) => ({
            ...plan,
            isPrimary: primaryCropPlan?.planting_plan?.id === plan.id,
        }));
    }, [plantingPlansRequest?.data, primaryCropPlan]);

    const deletePlanMutation = useMutateRequest<PlantingPlan>({
        successMessage: "Planting Plan Deleted",
        onSuccess: () => {
            plantingPlansRequest.refetch();
        },
    });

    const { showConfirm } = useConfirm();

    const deletePlan = useCallback(
        (plan: PlantingPlan) => {
            showConfirm({
                title: "Delete Planting Plan",
                description:
                    "Are you sure you want to delete this planting plan?",
                onConfirm: async () => {
                    await deletePlanMutation.mutateAsync({
                        url: `/planting_plans/${plan.id}/`,
                        method: "delete",
                    });
                },
            });
        },
        [deletePlanMutation, showConfirm],
    );

    const plantingColumns: ColumnDef<PlantingPlan & { isPrimary: boolean }>[] =
        useMemo(() => {
            return [
                plantingPlanColumns.selection(),
                plantingPlanColumns.name(),
                plantingPlanColumns.organization(organizations),
                plantingPlanColumns.crop_configuration(cropOptions),
                plantingPlanColumns.date(),
                plantingPlanColumns.seeding_rate(),
                plantingPlanColumns.seeding_depth(),
                {
                    id: "isPrimary",
                    header: "Primary",
                    accessorFn: (row) => row.isPrimary,
                    cell: ({ getValue }) =>
                        getValue() ? (
                            <div>
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    color="green"
                                />
                            </div>
                        ) : null,
                    size: 70,
                },
                {
                    id: "actions",
                    header: "Actions",
                    enableSorting: false,
                    cell: ({ row }) => {
                        const isExpanded = row.getIsExpanded();
                        return (
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    color="primary"
                                    onPress={() => {
                                        if (isExpanded) {
                                            setEditPlantingPlanId(null);
                                        } else {
                                            setAddNewPlan(false);
                                            handleEditPlantingPlan(
                                                row.original,
                                            );
                                        }
                                    }}
                                >
                                    {isExpanded ? "Cancel" : "Edit"}
                                </Button>
                                <Button
                                    size="sm"
                                    onPress={() => {
                                        if (row.original.id) {
                                            const newPlanData = {
                                                ...row.original,
                                                id: null,
                                                name: `${row.original.name} Copy`,
                                                organization:
                                                    userOrganizationId,
                                            };
                                            savePlantingPlanMutation.mutate(
                                                {
                                                    url: `/planting_plans/`,
                                                    method: "POST",
                                                    data: newPlanData,
                                                },
                                                {
                                                    onSuccess: (newPlan) => {
                                                        plantingPlansRequest
                                                            .refetch()
                                                            .then(() => {
                                                                setSelectedPlantingPlanId(
                                                                    newPlan.id,
                                                                );
                                                            });
                                                    },
                                                },
                                            );
                                        }
                                    }}
                                    variant="light"
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </Button>
                                {userIsSentinel && (
                                    <Button
                                        size="sm"
                                        isIconOnly
                                        variant="light"
                                        color="danger"
                                        onPress={() => {
                                            deletePlan(row.original);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                )}
                            </div>
                        );
                    },
                    size: 200,
                },
            ];
        }, [
            cropOptions,
            deletePlan,
            handleEditPlantingPlan,
            organizations,
            plantingPlansRequest,
            savePlantingPlanMutation,
            setSelectedPlantingPlanId,
            userIsSentinel,
            userOrganizationId,
        ]);

    const renderExpandedRow = useCallback(
        (toggleExpanded: () => void) => {
            return (
                <FormProvider {...plantingPlanForm}>
                    <form className="flex flex-col gap-1">
                        <div className="flex flex-row w-full justify-end gap-2 items-center mt-2">
                            <Button
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="mr-1"
                                    />
                                }
                                color="primary"
                                size="sm"
                                isDisabled={!userCanEditPlantingPlan}
                                isLoading={savePlantingPlanMutation.isPending}
                                onPress={() => {
                                    plantingPlanForm.trigger();
                                    plantingPlanForm.handleSubmit(
                                        handleSavePlantingPlan,
                                    )();
                                }}
                            >
                                Save Planting Plan
                            </Button>
                            <Button
                                variant="bordered"
                                size="sm"
                                onPress={() => {
                                    setEditPlantingPlanId(null);
                                    toggleExpanded();
                                }}
                                isDisabled={savePlantingPlanMutation.isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                        <div className="flex flex-row justify-between gap-1">
                            <TextInput
                                name="name"
                                label="Plan Name"
                                labelPlacement="outside"
                                size="sm"
                            />
                            {userIsSentinel && (
                                <AutoCompleteInput
                                    name="organization"
                                    label="Organization"
                                    labelPlacement="outside"
                                    options={organizations}
                                    size="sm"
                                />
                            )}
                            <SelectInput
                                name="crop_configuration"
                                label="Crop"
                                labelPlacement="outside"
                                options={cropOptions}
                                size="sm"
                            />
                            <DateInput
                                name="date"
                                label="Planting Date"
                                labelPlacement="outside"
                                size="sm"
                                minValue={twoMonthsAgo}
                            />
                            <NumberInput
                                name="crm"
                                label="CRM"
                                labelPlacement="outside"
                                size="sm"
                            />
                            <TextInput
                                name="hybrid"
                                label="Hybrid/Variety"
                                labelPlacement="outside"
                                size="sm"
                            />
                            <NumberInput
                                name="seeding_rate"
                                label="Seeding Rate"
                                labelPlacement="outside"
                                size="sm"
                            />
                            <NumberInput
                                name="seeding_depth"
                                label="Seeding Depth"
                                labelPlacement="outside"
                                size="sm"
                            />
                        </div>
                    </form>
                </FormProvider>
            );
        },
        [
            cropOptions,
            handleSavePlantingPlan,
            organizations,
            plantingPlanForm,
            savePlantingPlanMutation.isPending,
            twoMonthsAgo,
            userCanEditPlantingPlan,
            userIsSentinel,
        ],
    );

    const getIsRowExpanded = useCallback(
        (row: Row<PlantingPlan & { isPrimary: boolean }>) => {
            if (addNewPlan || editPlantingPlanId === null) return false;
            return row.original.id === editPlantingPlanId;
        },
        [addNewPlan, editPlantingPlanId],
    );

    const rowSelection = useMemo(() => {
        if (!selectedPlantingPlanId) return {};
        return {
            [selectedPlantingPlanId?.toString()]: true,
        };
    }, [selectedPlantingPlanId]);

    const getRowId = useCallback((row: PlantingPlan) => row.id.toString(), []);

    const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
        (updaterOrValue: Updater<RowSelectionState>) => {
            if (typeof updaterOrValue === "function") {
                const value = updaterOrValue(rowSelection);
                const keys = Object.keys(value);
                if (keys.length > 0) {
                    setSelectedPlantingPlanId(parseInt(keys[0]));
                } else {
                    setSelectedPlantingPlanId(null);
                }
            }
        },
        [rowSelection, setSelectedPlantingPlanId],
    );

    return (
        <>
            <div className="flex flex-col w-full gap-2">
                <ExpandingTable
                    data={data}
                    columns={plantingColumns}
                    sorting={[]}
                    setSorting={() => {}}
                    renderExpandedRow={renderExpandedRow}
                    enableMultiRowSelection={false}
                    getIsRowExpanded={getIsRowExpanded}
                    getRowId={getRowId}
                    onRowSelectionChange={onRowSelectionChange}
                    rowSelection={rowSelection}
                />
                <div>
                    {!addNewPlan && (
                        <Button
                            size="sm"
                            color="primary"
                            onPress={() => {
                                plantingPlanForm.reset({
                                    id: null,
                                    organization: userOrganizationId,
                                    name: "New Planting Plan",
                                    date: defaultPlantingDate,
                                    crop_configuration:
                                        DEFAULT_CROP_CONFIGURATION_ID,
                                    seeding_rate: DEFAULT_SEEDING_RATE,
                                    seeding_depth: DEFAULT_PLANTING_DEPTH,
                                    hybrid: null,
                                    crm: 100,
                                });
                                setAddNewPlan(true);
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            New Planting Plan
                        </Button>
                    )}
                    {addNewPlan && (
                        <div>
                            <div className="font-semibold text-lg ">
                                New Planting Plan
                            </div>
                            {renderExpandedRow(() => setAddNewPlan(false))}
                        </div>
                    )}
                </div>

                <div className="flex flex-col  h-full gap-1">
                    {activePlantingOperationId && (
                        <Button
                            size="sm"
                            color="primary"
                            fullWidth
                            isDisabled={!activePlantingOperationId}
                            isLoading={plantingOperationRequest.isLoading}
                            onPress={() => {
                                if (!plantingOperationRequest.data) return;
                                plantingPlanForm.reset({
                                    id: null,
                                    organization: userOrganizationId,
                                    name: "Import Logged Operation",
                                    date: dayjs(
                                        plantingOperationRequest.data
                                            .start_date,
                                    ).toDate(),
                                    crop_configuration:
                                        plantingOperationRequest.data
                                            .crop_configuration.id,
                                    seeding_rate:
                                        plantingOperationRequest.data
                                            .average_seeding_rate,
                                    seeding_depth:
                                        plantingOperationRequest.data
                                            .average_planting_depth,
                                    hybrid:
                                        plantingOperationRequest.data
                                            .varieties[0]?.name || "",
                                    crm:
                                        Number(
                                            plantingOperationRequest.data
                                                .varieties[0]?.crm,
                                        ) || undefined,
                                });
                            }}
                        >
                            <FontAwesomeIcon icon={faArrowRightToBracket} />
                            Import Logged Operation
                        </Button>
                    )}
                </div>
            </div>
        </>
    );
}
